import { ProctoringOption, ProctoringPlatforms } from "@verity/proctoring-option/types";
import { PROCTORING_OPTION_TEXT, ProctoringOptionCategory } from "../proctoring-options/option-text";
import { useMobileLayout } from "../../hooks";
import { CoreModal } from "../../core";
import { Typography } from "@mui/material";
import OneDot from "../images/one-dot.svg?react";

type UnsupportedModalProps = {
	open: boolean;
	onClose: () => void;
	onConfirm: () => void;
	platformUnsupportedOptionNames: {
		[P in keyof ProctoringPlatforms | "none" | "passwordConflict"]?: {
			[P in ProctoringOptionCategory]?: Array<{
				name: string;
				option: keyof ProctoringOption;
			}>;
		};
	};
	onEnableAnyway?: () => void;
	enableAnyway?: boolean;
};

export default function UnsupportedModal({
	open,
	onClose,
	onConfirm,
	platformUnsupportedOptionNames,
	onEnableAnyway,
	enableAnyway = false,
}: UnsupportedModalProps) {
	const isMobileLayout = useMobileLayout();
	const iconProps = {
		style: { color: "#42296e" },
		width: "24px",
		height: "24px",
	};

	const getUnsupportedModalDescriptionText = () => {
		const platforms = Object.keys(platformUnsupportedOptionNames) as Array<keyof typeof platformUnsupportedOptionNames>;
		if (platforms.length === 1) {
			if (platforms[0] === "isExtension") {
				return `The ${PROCTORING_OPTION_TEXT[platforms[0]].header} does not support the restrictions listed below. Continuing will deactivate these restrictions. Are you sure you want to continue?`;
			} else if (platforms[0] !== "none" && platforms[0] !== "passwordConflict") {
				return `The ${PROCTORING_OPTION_TEXT[platforms[0]].header} app does not support the restrictions listed below. Continuing will deactivate these restrictions. Are you sure you want to continue?`;
			} else if (platforms[0] === "passwordConflict") {
				return `Setting an assessment password will not allow right-click to be disabled. This allows test-takers to automatically inject the password for their assessments. Do you want to disable the feature and continue?`;
			} else {
				return `The selected proctoring platform configuration does not support the restrictions listed below. Continuing will deactivate these restrictions. Are you sure you want to continue?`;
			}
		} else if (platforms.length > 1) {
			return `The following proctoring platforms do not support the restrictions listed below.`;
		}
	};

	return (
		<CoreModal
			open={open}
			widthFixed
			minWidth={!isMobileLayout ? "min(760px, 80%)" : undefined}
			maxWidth={isMobileLayout ? "95%" : undefined}
			maxHeight="min(640px, 80%)"
			onClose={onClose}
			title="Invalid Proctoring Features"
			buttons={[
				{
					text: enableAnyway ? "Enable Anyway" : "Cancel",
					color: "secondary",
					onClick: enableAnyway ? onEnableAnyway : onClose,
				},
				{ text: "Disable and Continue", onClick: onConfirm },
			]}
		>
			<Typography>{getUnsupportedModalDescriptionText()}</Typography>
			{Object.keys(platformUnsupportedOptionNames).map((p, i) => {
				const platform = p as keyof typeof platformUnsupportedOptionNames;
				const optionCategories = platformUnsupportedOptionNames[platform] ?? {};
				const multiplePlatforms = Object.keys(platformUnsupportedOptionNames).length > 1;
				return (
					<div key={i} style={{ padding: "0px 6px", color: "#000" }}>
						{multiplePlatforms && Object.keys(optionCategories).length > 0 && (
							<div style={{ marginTop: "16px" }}>
								{platform === "none" ? "Other" : PROCTORING_OPTION_TEXT.isExtension.header}:
							</div>
						)}
						{Object.keys(optionCategories).map((c, j) => {
							const category = c as ProctoringOptionCategory;
							return (
								<div key={j} style={multiplePlatforms ? { padding: "0px 12px" } : undefined}>
									<Typography
										variant="h2"
										fontSize={18}
										fontWeight={700}
										style={{ marginTop: j > 0 ? "32px" : "16px" }}
									>
										{category}
									</Typography>
									<div
										style={{
											height: "0.5px",
											width: "100%",
											backgroundColor: "#a6a6a6",
											margin: "6px 0",
										}}
									/>
									<div>
										{optionCategories[category]?.map((option, k) => (
											<div
												key={k}
												style={{
													display: "flex",
													padding: "4px 0",
													gap: "20px",
												}}
											>
												<div
													style={{
														flexBasis: "35%",
														display: "flex",
														alignItems: "center",
														gap: "6px",
														maxWidth: "200px",
													}}
												>
													<div style={{ width: "24px", height: "24px" }}>
														{PROCTORING_OPTION_TEXT[option.option].icon?.(iconProps) ?? <OneDot {...iconProps} />}
													</div>
													<Typography fontWeight={700}>{option.name}</Typography>
												</div>
												<div
													style={{
														flexBasis: "65%",
														display: "flex",
														alignItems: "center",
													}}
												>
													<Typography>{PROCTORING_OPTION_TEXT[option.option].description}</Typography>
												</div>
											</div>
										))}
									</div>
								</div>
							);
						})}
					</div>
				);
			})}
		</CoreModal>
	);
}
